import { daysCounter } from '@oskecare/fe-utilities/scripts/helpers.js';
import BillingPeriod from './BillingPeriod.js';
import {
  BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATED,
  BILLING_DOCUMENT_ATTACHMENT_STATUS_NOT_GENERATED,
  BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATING,
  BILLING_DOCUMENT_STATE_PARTIALLY_PAID,
  BILLING_DOCUMENT_STATE_SENT,
  BILLING_DOCUMENT_STATUS_PAID,
  BILLING_DOCUMENT_STATUS_NOT_PAID,
  BILLING_DOCUMENT_STATUS_PARTIALLY_PAID,
  DELAY_FOR_HTML_PDF_GENERATION,
  BILLING_DOCUMENT_MIME_TYPE_HTML,
  BILLING_DOCUMENT_MIME_TYPE_PDF,
  ONLYCN_TYPE_ID,
  ONLYSN_TYPE_ID,
  INV_MAIN_CONS,
} from '../constants.js';

/**
 * Groups customers invoices/documents into one billing period
 *
 * @property {BillingPeriod} period
 * @property {BillingDocument[]} items
 * @property {string} _invoiceBaseUrl
 */
export default class BillingListItem {
  /**
   * @param {{startDateTime: string, endDateTime: string}} period
   * @param {BillingDocument[]} items
   * @param {string} invoiceBaseUrl
   */
  constructor({ period, items, invoiceBaseUrl }) {
    this.period = new BillingPeriod(period);
    this.items = items;
    this._invoiceBaseUrl = invoiceBaseUrl;
  }

  /**
   * @returns {number}
   */
  get itemsCount() {
    return this.items.length;
  }

  /**
   * @returns {number}
   */
  get amountTotal() {
    return this.items.reduce((sum, item) => sum + item.amountDue, 0);
  }

  /**
   * @returns {number}
   */
  get amountRemaining() {
    return this.items.reduce((sum, item) => sum + item.amountRemaining, 0);
  }

  /**
   * Due date of the whole billing period is evaluated as due date of
   * the oldest unpaid document or due date of the latest document in case all of the documents are paid
   *
   * TODO: investigate why BILLING_DOCUMENT_STATE_SENT is used instead of BILLING_DOCUMENT_STATUS_NOT_PAID, this may be a bug
   *
   * @returns {Date}
   */
  get dueDate() {
    const sortedItemsByDueDate = this.items.toSorted((a, b) => b.dueDate - a.dueDate);
    return (
      sortedItemsByDueDate.find(
        item =>
          (item.state === BILLING_DOCUMENT_STATE_PARTIALLY_PAID ||
            item.state === BILLING_DOCUMENT_STATE_SENT) &&
          item.amountDue > 0,
      )?.dueDate ?? sortedItemsByDueDate[0].dueDate
    );
  }

  /**
   * @returns {number}
   */
  get paymentGroupStatus() {
    if (this.items.every(item => item.isPaid)) {
      return BILLING_DOCUMENT_STATUS_PAID;
    }
    if (this.items.every(item => item.isUnpaid)) {
      return BILLING_DOCUMENT_STATUS_NOT_PAID;
    }
    return BILLING_DOCUMENT_STATUS_PARTIALLY_PAID;
  }

  /**
   * @returns {?{id: string, name: string, mimeType: string, url:string}}
   */
  get _invoiceGroupPdf() {
    const consolidatedInvoice =
      this.items
        .filter(item => item.isBasicInvoice)
        .map(item => item.billDocument)
        .flat()
        .filter(
          document =>
            document.mimeType === BILLING_DOCUMENT_MIME_TYPE_PDF &&
            !document.id.includes(ONLYSN_TYPE_ID) &&
            !document.id.includes(ONLYCN_TYPE_ID),
        )
        .find(
          document =>
            document.id.match(/^INV_MAIN_.*_PARENT/) || document.id.includes(INV_MAIN_CONS),
        ) ?? null;

    if (consolidatedInvoice) {
      return consolidatedInvoice;
    }

    return (
      this.items
        .map(item => item.billDocument)
        .flat()
        .find(
          document =>
            document.mimeType === BILLING_DOCUMENT_MIME_TYPE_PDF &&
            !document.id.includes(ONLYSN_TYPE_ID) &&
            !document.id.includes(ONLYCN_TYPE_ID),
        ) ?? null
    );
  }

  /**
   * @return {string}
   */
  get invoiceGroupPdfUrl() {
    if (!this._invoiceGroupPdf) {
      return '';
    }
    return `${this._invoiceBaseUrl}/${this._invoiceGroupPdf.id}/${this._invoiceGroupPdf.name}/pdf`;
  }

  /**
   * @returns {(0|1|-1)}
   */
  get invoiceGroupPdfStatus() {
    if (this.invoiceGroupPdfUrl) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATED;
    }

    if (daysCounter(this.items[0].billDate) < DELAY_FOR_HTML_PDF_GENERATION) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATING;
    }

    return BILLING_DOCUMENT_ATTACHMENT_STATUS_NOT_GENERATED;
  }

  /**
   * @returns {?{id: string, name: string, mimeType: string, url:string}}
   */
  get _invoiceGroupHtml() {
    const consolidatedInvoice =
      this.items
        .filter(item => item.isBasicInvoice)
        .map(item => item.billDocument)
        .flat()
        .filter(
          document =>
            document.mimeType === BILLING_DOCUMENT_MIME_TYPE_HTML &&
            !document.id.includes(ONLYSN_TYPE_ID) &&
            !document.id.includes(ONLYCN_TYPE_ID),
        )
        .find(
          document =>
            document.id.match(/^INV_MAIN_.*_PARENT/) || document.id.includes(INV_MAIN_CONS),
        ) ?? null;

    if (consolidatedInvoice) {
      return consolidatedInvoice;
    }

    return (
      this.items
        .map(item => item.billDocument)
        .flat()
        .find(
          document =>
            document.mimeType === BILLING_DOCUMENT_MIME_TYPE_HTML &&
            !document.id.includes(ONLYSN_TYPE_ID) &&
            !document.id.includes(ONLYCN_TYPE_ID),
        ) ?? null
    );
  }

  /**
   * @return {string}
   */
  get invoiceGroupHtmlUrl() {
    if (!this._invoiceGroupHtml) {
      return '';
    }
    return `${this._invoiceBaseUrl}/${this._invoiceGroupHtml.id}/${this._invoiceGroupHtml.name}/html`;
  }

  /**
   * @returns {(0|1|-1)}
   */
  get invoiceGroupHtmlStatus() {
    if (this.invoiceGroupHtmlUrl) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATED;
    }

    if (daysCounter(this.items[0].billDate) < DELAY_FOR_HTML_PDF_GENERATION) {
      return BILLING_DOCUMENT_ATTACHMENT_STATUS_GENERATING;
    }

    return BILLING_DOCUMENT_ATTACHMENT_STATUS_NOT_GENERATED;
  }
}
